import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { v4 as uuid } from 'uuid';

export function ModalMovimientos({
  open,
  onClose,
  tipo = 'ENTRADA', // ENTRADA, VENTA, SALIDA
  subTipo = null,
  fechaSeleccionada = null,
  state,
  productos,
  díaBloqueado = false,
  subirFuturo = null
}) {
  const [metodoPago, setMetodoPago] = useState('EFECTIVO');
  const [filaActual, setFilaActual] = useState({
    prodId: '',
    cant: '',
    precio: '',
    total: ''
  });
  const [carrito, setCarrito] = useState([]);

  const handleCantidadChange = (e) => {
    const cant = e.target.value;
    setFilaActual(prev => ({
      ...prev,
      cant,
      total: cant && prev.precio ? (parseFloat(cant) * parseFloat(prev.precio)).toFixed(2) : ''
    }));
  };

  const handlePrecioUnitarioChange = (e) => {
    const precio = e.target.value;
    setFilaActual(prev => ({
      ...prev,
      precio,
      total: prev.cant && precio ? (parseFloat(prev.cant) * parseFloat(precio)).toFixed(2) : ''
    }));
  };

  const handleTotalChange = (e) => {
    const total = e.target.value;
    setFilaActual(prev => ({
      ...prev,
      total,
      precio: prev.cant && total ? (parseFloat(total) / parseFloat(prev.cant)).toFixed(2) : ''
    }));
  };

  const agregarAlCarrito = () => {
    if (!filaActual.prodId || !filaActual.cant) {
      alert('Debe completar producto y cantidad');
      return;
    }

    // SALIDA puro: precio 0 automático. ENTRADA/VENTA: precio requerido
    if ((tipo !== 'SALIDA' || subTipo) && !filaActual.precio) {
      alert('Debe completar precio');
      return;
    }

    const pNombre = productos.find(p => p.id === filaActual.prodId)?.nombre;
    const precio = tipo === 'SALIDA' && !subTipo ? 0 : parseFloat(filaActual.precio);
    const cant = parseFloat(filaActual.cant);
    const total = cant * precio;

    setCarrito(prev => [
      ...prev,
      { ...filaActual, nombre: pNombre, precio, cantidad: cant, total }
    ]);

    setFilaActual({ prodId: '', cant: '', precio: '', total: '' });
  };

  const eliminarDelCarrito = (index) => {
    setCarrito(prev => prev.filter((_, i) => i !== index));
  };

  const handleGuardarTodo = async () => {
    if (díaBloqueado) {
      alert('⛔ No puedes registrar movimientos cuando hay un arqueo completado en el día');
      return;
    }

    if (carrito.length === 0) return;

    const tipoFinal = tipo === 'SALIDA' ? (subTipo ? 'VENTA' : 'SALIDA') : tipo;
    // SALIDA puro: no usa metodoPago (no hay transacción monetaria)
    const metodoPagoFinal = (tipo === 'SALIDA' && !subTipo) ? null : metodoPago;

    for (const item of carrito) {
      const transactionID = uuid();
      const resultado = subirFuturo
        ? await state.registrarMovimiento(item.prodId, item.cantidad, item.precio, tipoFinal, transactionID, subTipo, metodoPagoFinal, fechaSeleccionada, subirFuturo)
        : await state.registrarMovimiento(item.prodId, item.cantidad, item.precio, tipoFinal, transactionID, subTipo, metodoPagoFinal, fechaSeleccionada);

      if (!resultado) return;
    }

    handleClose();
  };

  const handleClose = () => {
    onClose();
    setCarrito([]);
    setFilaActual({ prodId: '', cant: '', precio: '', total: '' });
    setMetodoPago('EFECTIVO');
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Nueva {tipo === 'SALIDA' && subTipo ? (subTipo === 'VENTA' ? 'VENTA' : 'SALIDA') : tipo}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Stack spacing={2}>
          {!(tipo === 'SALIDA' && !subTipo) && 
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Método de Pago
                </Typography>
                <ToggleButtonGroup
                value={metodoPago}
                exclusive
                onChange={(e, val) => val && setMetodoPago(val)}
                fullWidth
                >
                <ToggleButton value="EFECTIVO">Efectivo</ToggleButton>
                <ToggleButton value="QR">QR</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            }

          <Divider />

          <Select
            fullWidth
            value={filaActual.prodId}
            onChange={e => setFilaActual({ ...filaActual, prodId: e.target.value })}
            displayEmpty
          >
            <MenuItem value="" disabled>Selecciona un Producto</MenuItem>
            {productos.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
            ))}
          </Select>

          <TextField
            label="Cantidad"
            type="number"
            fullWidth
            value={filaActual.cant}
            onChange={handleCantidadChange}
            inputProps={{ step: '0.01' }}
          />

          {/* SALIDA puro: No mostrar campos de precio. ENTRADA/VENTA: Mostrar precio */}
          {!(tipo === 'SALIDA' && !subTipo) && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Precio Unitario"
                type="number"
                fullWidth
                value={filaActual.precio}
                onChange={handlePrecioUnitarioChange}
                inputProps={{ step: '0.01' }}
                helperText="o ingresa el Total →"
              />

              {(tipo === 'ENTRADA' || (tipo === 'SALIDA' && subTipo === 'VENTA')) && (
                <TextField
                  label="Total"
                  type="number"
                  fullWidth
                  value={filaActual.total}
                  onChange={handleTotalChange}
                  inputProps={{ step: '0.01' }}
                  helperText="o ingresa Unitario ←"
                />
              )}
            </Stack>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={agregarAlCarrito}
            startIcon={<AddCircleIcon />}
          >
            Agregar al Carrito
          </Button>

          <Divider />

          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {carrito.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                El carrito está vacío
              </Typography>
            ) : (
              carrito.map((item, i) => (
                <Stack
                  key={i}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1.5}
                  sx={{ bgcolor: '#f5f5f5', mb: 1, borderRadius: 1 }}
                >
                  <Typography variant="body2">
                    {item.nombre} • {item.cantidad} x {item.precio.toFixed(2)}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {item.total.toFixed(2)}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => eliminarDelCarrito(i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>
              ))
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 2, p: 2 }}>
        <Box sx={{ width: '100%', textAlign: 'right' }}>
          <Typography variant="h6">
            Total: {carrito.reduce((s, i) => s + i.total, 0).toFixed(2)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <Button fullWidth variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={díaBloqueado || !carrito.length}
            onClick={handleGuardarTodo}
          >
            Confirmar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
